/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import type GroupType from "@/model/group";
import { STORAGE, STORAGE_KEY } from "@/constants";
import { getItem, setItem, removeItem } from "@/lib/storage-utils";

type GroupContextType = {
  groupData: GroupType | null;
  setGroupData: (groupData: GroupType | null) => void;
  readCachedGroupDetail: () => void;
};

const GroupContext = createContext<GroupContextType | null>(null);

const GroupProvider = ({ children }: { children: React.ReactNode }) => {
  const [groupData, setGroupData] = useState<GroupType | null>(() => {
    const storedGroupData = getItem(STORAGE_KEY.GROUP_DATA, STORAGE.SESSION);
    return storedGroupData || null;
  });

  // Function to sync from sessionStorage (called after loader redirects)
  const readCachedGroupDetail = useCallback(() => {
    const storedGroupData = getItem(STORAGE_KEY.GROUP_DATA, STORAGE.SESSION);
    if (storedGroupData) {
      setGroupData((currentData) => {
        if (JSON.stringify(storedGroupData) !== JSON.stringify(currentData)) {
          return storedGroupData;
        }
        return currentData;
      });
    } else {
      setGroupData(null);
    }
  }, []);

  useEffect(() => {
    readCachedGroupDetail();
  }, [readCachedGroupDetail]);

  useEffect(() => {
    if (groupData) {
      setItem(STORAGE_KEY.GROUP_DATA, groupData, STORAGE.SESSION);
    } else {
      removeItem(STORAGE_KEY.GROUP_DATA, STORAGE.SESSION);
    }
  }, [groupData]);

  return (
    <GroupContext.Provider
      value={{ groupData, setGroupData, readCachedGroupDetail }}
    >
      {children}
    </GroupContext.Provider>
  );
};

const useGroupProvider = () => {
  const context = useContext(GroupContext);
  if (!context) {
    throw new Error("useGroup must be used within a GroupProvider");
  }
  return context;
};

export { GroupProvider, useGroupProvider };
